import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Settings2, Save, RefreshCw, CalendarDays, CalendarCheck, Timer, Paperclip, Info, IdCard, ArrowRight, Plane,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiErrorMessage } from "@/lib/api";
import { KlaimKonfigSection } from "@/pages/admin/KlaimKonfigSection";
import { PayrollKonfigSection } from "@/pages/admin/PayrollKonfigSection";
import { PayrollCsvKonfigSection } from "@/pages/admin/PayrollCsvKonfigSection";
import { ReportSignatureKonfigSection } from "@/pages/admin/ReportSignatureKonfigSection";

const JENIS_ORDER = [
  "Cuti",
  "Izin",
  "Sakit",
  "Istirahat Haid",
  "Istirahat Melahirkan / Gugur Kandungan",
  "Dinas Luar",
  "Tugas Luar",
];

export default function AdminKonfigurasi() {
  const [loading, setLoading] = useState(true);
  const [durasi, setDurasi] = useState({});
  const [durasiSaving, setDurasiSaving] = useState(null); // jenis being saved
  const [kuotaCuti, setKuotaCuti] = useState("12");
  const [savingCuti, setSavingCuti] = useState(false);
  const [kuotaLembur, setKuotaLembur] = useState("30");
  const [cutoffLembur, setCutoffLembur] = useState("1");
  const [savingLembur, setSavingLembur] = useState(false);
  const [maxLampiran, setMaxLampiran] = useState("2");
  const [savingLampiran, setSavingLampiran] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [dm, sc, so, sp] = await Promise.all([
        api.get("/settings/durasi-max"),
        api.get("/settings/cuti"),
        api.get("/settings/overtime"),
        api.get("/settings/pengajuan"),
      ]);
      // build local map ordered by JENIS_ORDER
      const durasiMap = {};
      JENIS_ORDER.forEach((j) => {
        if (dm.data[j] != null) durasiMap[j] = String(dm.data[j]);
      });
      setDurasi(durasiMap);
      setKuotaCuti(String(sc.data.kuota_default_tahunan));
      setKuotaLembur(String(so.data.kuota_bulanan_jam));
      setCutoffLembur(String(so.data.cutoff_start_day ?? 1));
      setMaxLampiran(String(sp.data.max_lampiran_mb));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function saveDurasi(jenis) {
    const v = parseInt(durasi[jenis], 10);
    if (isNaN(v) || v <= 0 || v > 365) {
      toast.error("Durasi harus 1-365 hari");
      return;
    }
    setDurasiSaving(jenis);
    try {
      await api.put("/settings/durasi-max", { jenis, max_days: v });
      toast.success(`Durasi ${jenis} disimpan (${v} hari)`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setDurasiSaving(null);
    }
  }

  async function saveCuti() {
    const v = parseInt(kuotaCuti, 10);
    if (isNaN(v) || v <= 0 || v > 60) {
      toast.error("Kuota cuti harus 1-60 hari");
      return;
    }
    setSavingCuti(true);
    try {
      await api.put("/settings/cuti", { kuota_default_tahunan: v });
      toast.success("Kuota cuti default disimpan");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingCuti(false);
    }
  }

  async function saveLembur() {
    const v = parseFloat(kuotaLembur);
    if (isNaN(v) || v <= 0 || v > 200) {
      toast.error("Kuota lembur harus 0.5-200 jam");
      return;
    }
    const c = parseInt(cutoffLembur, 10);
    if (isNaN(c) || c < 1 || c > 28) {
      toast.error("Cutoff harus tanggal 1-28");
      return;
    }
    setSavingLembur(true);
    try {
      await api.put("/settings/overtime", {
        kuota_bulanan_jam: v,
        cutoff_start_day: c,
      });
      toast.success("Kuota & cutoff lembur disimpan");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingLembur(false);
    }
  }

  async function saveLampiran() {
    const v = parseFloat(maxLampiran);
    if (isNaN(v) || v <= 0 || v > 25) {
      toast.error("Batas lampiran harus 0.1-25 MB");
      return;
    }
    setSavingLampiran(true);
    try {
      await api.put("/settings/pengajuan", { max_lampiran_mb: v });
      toast.success("Batas lampiran disimpan");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingLampiran(false);
    }
  }

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="page-konfigurasi">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Administrasi
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Konfigurasi Kebijakan
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Pusat pengaturan business policy — durasi, kuota, dan batas lampiran. Perubahan berlaku segera untuk pengajuan baru.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="konfig-refresh-btn">
          <RefreshCw size={14} /> Muat Ulang
        </Button>
      </div>

      {loading ? (
        <div className="mt-8 text-center text-slate-500 py-14">Memuat konfigurasi…</div>
      ) : (
        <div className="mt-6 space-y-6">
          {/* Shortcut: Master Data Kepegawaian — sub-menu baru (Feb 2026) */}
          <Link
            to="/admin/master-kepegawaian"
            className="group block rounded-xl border border-teal-200 bg-teal-50/60 hover:bg-teal-50 p-5 transition-colors"
            data-testid="link-master-kepegawaian"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-white border border-teal-200 flex items-center justify-center text-teal-700 shrink-0 group-hover:scale-105 transition-transform">
                <IdCard size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="font-display text-lg font-extrabold text-slate-900 leading-tight">
                    Master Data Kepegawaian
                  </h2>
                  <ArrowRight size={16} className="text-teal-700 group-hover:translate-x-0.5 transition-transform" />
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Kelola Status Kepegawaian, Bidang Kerja, Jabatan, Kode Pangkat, dan Tingkat Jabatan — digunakan sebagai dropdown pada form data pegawai.
                </p>
              </div>
            </div>
          </Link>
          {/* Shortcut: Konfigurasi SPPD — sub-menu baru (Oct 2026) */}
          <Link
            to="/admin/konfigurasi-sppd"
            className="group block rounded-xl border border-sky-200 bg-sky-50/60 hover:bg-sky-50 p-5 transition-colors"
            data-testid="link-konfigurasi-sppd"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-white border border-sky-200 flex items-center justify-center text-sky-700 shrink-0 group-hover:scale-105 transition-transform">
                <Plane size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="font-display text-lg font-extrabold text-slate-900 leading-tight">
                    Perjalanan Dinas (SPPD)
                  </h2>
                  <ArrowRight size={16} className="text-sky-700 group-hover:translate-x-0.5 transition-transform" />
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Kelola penandatangan Surat Tugas &amp; Form SPPD, kualifikasi jarak, komponen biaya, serta matriks tarif per kombinasi jarak × jabatan.
                </p>
              </div>
            </div>
          </Link>
          {/* Section 1 — Durasi Max per Jenis */}
          <SectionCard
            icon={CalendarDays}
            title="Durasi Maksimum per Jenis Pengajuan"
            description="Batas maksimum jumlah hari untuk setiap jenis pengajuan. Contoh: Istirahat Melahirkan / Gugur Kandungan mengikuti UU Ketenagakerjaan Pasal 82 (~90 hari)."
            testId="section-durasi"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {JENIS_ORDER.map((jenis) => (
                <div key={jenis} data-testid={`durasi-row-${jenis}`}>
                  <Label className="text-xs font-bold text-slate-600 flex items-center justify-between gap-2">
                    <span className="truncate">{jenis}</span>
                    <span className="text-[10px] font-normal text-slate-400">hari</span>
                  </Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={durasi[jenis] ?? ""}
                      onChange={(e) => setDurasi({ ...durasi, [jenis]: e.target.value })}
                      data-testid={`durasi-input-${jenis}`}
                    />
                    <Button
                      size="sm"
                      onClick={() => saveDurasi(jenis)}
                      disabled={durasiSaving === jenis}
                      className="bg-teal-700 hover:bg-teal-800 gap-1.5 shrink-0"
                      data-testid={`durasi-save-${jenis}`}
                    >
                      <Save size={13} /> {durasiSaving === jenis ? "…" : "Simpan"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Section 2 — Kuota Cuti Tahunan */}
          <SectionCard
            icon={CalendarCheck}
            title="Kuota Cuti Tahunan (Default Global)"
            description="Kuota cuti standar per pegawai per tahun. Pegawai tertentu dapat memiliki override individu di Master Pegawai."
            testId="section-cuti"
          >
            <div className="max-w-xs">
              <Label className="text-xs font-bold text-slate-600 flex items-center justify-between gap-2">
                <span>Hari per Tahun</span>
                <span className="text-[10px] font-normal text-slate-400">1-60</span>
              </Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={kuotaCuti}
                  onChange={(e) => setKuotaCuti(e.target.value)}
                  data-testid="input-konfig-kuota-cuti"
                />
                <Button
                  onClick={saveCuti}
                  disabled={savingCuti}
                  className="bg-teal-700 hover:bg-teal-800 gap-1.5 shrink-0"
                  data-testid="save-konfig-kuota-cuti"
                >
                  <Save size={13} /> {savingCuti ? "…" : "Simpan"}
                </Button>
              </div>
            </div>
          </SectionCard>

          {/* Section 3 — Kuota Lembur Bulanan */}
          <SectionCard
            icon={Timer}
            title="Kuota Lembur Bulanan"
            description="Batas total jam lembur aktual per pegawai per periode. Cutoff mengatur tanggal awal periode (contoh: cutoff 15 → periode 15 s/d 14 bulan berikutnya)."
            testId="section-lembur"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              <div>
                <Label className="text-xs font-bold text-slate-600 flex items-center justify-between gap-2">
                  <span>Jam Aktual per Periode</span>
                  <span className="text-[10px] font-normal text-slate-400">0.5-200</span>
                </Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="200"
                  value={kuotaLembur}
                  onChange={(e) => setKuotaLembur(e.target.value)}
                  className="mt-1.5"
                  data-testid="input-konfig-kuota-lembur"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-600 flex items-center justify-between gap-2">
                  <span>Cutoff Tanggal Awal</span>
                  <span className="text-[10px] font-normal text-slate-400">1-28 (1=kalender)</span>
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={cutoffLembur}
                  onChange={(e) => setCutoffLembur(e.target.value)}
                  className="mt-1.5"
                  data-testid="input-konfig-cutoff-lembur"
                />
              </div>
              <div className="sm:col-span-2">
                <Button
                  onClick={saveLembur}
                  disabled={savingLembur}
                  className="bg-teal-700 hover:bg-teal-800 gap-1.5"
                  data-testid="save-konfig-kuota-lembur"
                >
                  <Save size={13} /> {savingLembur ? "…" : "Simpan"}
                </Button>
              </div>
            </div>
          </SectionCard>

          {/* Section 4 — Max Lampiran */}
          <SectionCard
            icon={Paperclip}
            title="Batas Ukuran Lampiran"
            description="Ukuran maksimum file lampiran (medis/pendukung) yang dapat diunggah pegawai saat pengajuan."
            testId="section-lampiran"
          >
            <div className="max-w-xs">
              <Label className="text-xs font-bold text-slate-600 flex items-center justify-between gap-2">
                <span>Maksimum</span>
                <span className="text-[10px] font-normal text-slate-400">MB</span>
              </Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  type="number"
                  step="0.5"
                  min="0.1"
                  max="25"
                  value={maxLampiran}
                  onChange={(e) => setMaxLampiran(e.target.value)}
                  data-testid="input-konfig-max-lampiran"
                />
                <Button
                  onClick={saveLampiran}
                  disabled={savingLampiran}
                  className="bg-teal-700 hover:bg-teal-800 gap-1.5 shrink-0"
                  data-testid="save-konfig-max-lampiran"
                >
                  <Save size={13} /> {savingLampiran ? "…" : "Simpan"}
                </Button>
              </div>
            </div>
          </SectionCard>

          {/* Section 5 — Klaim Pengobatan */}
          <KlaimKonfigSection />

          {/* Section 6 — Periode Penggajian (Payroll) */}
          <PayrollKonfigSection />

          {/* Section 7 — Pengaturan CSV Payroll (Format Mandiri) */}
          <PayrollCsvKonfigSection />

          {/* Section 8 — Pengaturan Tanda Tangan Laporan (Export Excel) */}
          <ReportSignatureKonfigSection />

          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 text-xs text-amber-900" data-testid="konfig-info-note">
            <Info size={14} className="mt-0.5 shrink-0" />
            <div>
              <b>Catatan:</b> Perubahan berlaku untuk pengajuan yang <b>dibuat setelah</b> penyimpanan. Pengajuan yang sudah ada tidak diubah. Semua nilai divalidasi ulang di backend — modifikasi via API tanpa role Admin akan ditolak.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionCard({ icon: Icon, title, description, testId, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700 shrink-0">
          <Icon size={18} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-extrabold text-slate-900 leading-tight">{title}</h2>
          <p className="text-xs text-slate-500 mt-1">{description}</p>
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}
